import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { api } from '../api';
import { Link, useParams } from 'react-router-dom';
import { Button, Label, TextInput, Card, Alert, Table, Pagination } from 'flowbite-react';
import { useToast } from '../components/ToastProvider';

export default function Users() {
  const { appUuid: routeAppUuid } = useParams();
  const { addToast } = useToast();
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [appUuid, setAppUuid] = useState(routeAppUuid || '');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const load = useCallback(async () => {
    try {
      const qs = appUuid ? `?app_uuid=${encodeURIComponent(appUuid)}` : '';
      const data = await api.get(`/users${qs}`);
      setItems(data.items || []);
      addToast({ type: 'info', message: `Загружено пользователей: ${(data.items || []).length}` });
    } catch (e) {
      setError(e.message);
    }
  }, [appUuid, addToast]);

  useEffect(() => { setAppUuid(routeAppUuid || ''); }, [routeAppUuid]);
  useEffect(() => { load(); }, [load]);

  const applyFilter = async (e) => {
    e.preventDefault();
    setPage(1);
    await load();
  };

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const pageItems = useMemo(() => items.slice((page - 1) * pageSize, page * pageSize), [items, page]);

  return (
    <div className="grid gap-4">
      <Card>
        <h2 className="text-lg font-semibold">Пользователи</h2>
        {error && <Alert color="failure" className="mt-2">{error}</Alert>}
        {!routeAppUuid && (
          <form onSubmit={applyFilter} className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2 mb-3">
            <div className="flex-1">
              <Label htmlFor="app_uuid" value="app_uuid" />
              <TextInput id="app_uuid" placeholder="app_uuid" value={appUuid} onChange={e => setAppUuid(e.target.value)} />
            </div>
            <Button type="submit">Фильтровать</Button>
          </form>
        )}
        {/* Mobile cards */}
        <div className="grid gap-3 sm:hidden">
          {pageItems.length === 0 ? (
            <div className="text-sm text-gray-500">Пользователи не найдены</div>
          ) : (
            pageItems.map(u => (
              <Card key={u.id}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold break-words">{u.name || u.username || u.external_id}</div>
                    <div className="text-xs text-gray-500 mt-1 break-all">{u.external_id}</div>
                  </div>
                  <div className="text-right text-xs text-gray-600 whitespace-nowrap">{new Date(u.created_at).toLocaleString()}</div>
                </div>
                <div className="mt-3">
                  <Button size="xs" color="light" as={Link} to={`/users/${u.id}`}>Открыть</Button>
                </div>
              </Card>
            ))
          )}
        </div>
        {/* Desktop table */}
        <div className="overflow-x-auto hidden sm:block">
          <Table hoverable>
            <Table.Head>
              <Table.HeadCell>Пользователь</Table.HeadCell>
              <Table.HeadCell className="hidden sm:table-cell">Внешний ID</Table.HeadCell>
              <Table.HeadCell className="hidden md:table-cell">Приложение</Table.HeadCell>
              <Table.HeadCell className="hidden md:table-cell">Создан</Table.HeadCell>
              <Table.HeadCell>
                <span className="sr-only">Действия</span>
              </Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {pageItems.map(u => (
                <Table.Row key={u.id} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                  <Table.Cell>
                    <Link className="underline underline-offset-2 decoration-blue-500 hover:text-blue-700 break-words max-w-[200px] sm:max-w-none inline-block" to={`/users/${u.id}`}>{u.name || u.username || u.external_id}</Link>
                  </Table.Cell>
                  <Table.Cell className="hidden sm:table-cell font-mono text-xs text-gray-600 break-all">{u.external_id}</Table.Cell>
                  <Table.Cell className="hidden md:table-cell">{u.application_id ? `#${u.application_id}` : ''}</Table.Cell>
                  <Table.Cell className="hidden md:table-cell whitespace-nowrap">{new Date(u.created_at).toLocaleString()}</Table.Cell>
                  <Table.Cell></Table.Cell>
                </Table.Row>
              ))}
              {pageItems.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={5} className="text-center text-sm text-gray-500">Пользователи не найдены</Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
        <div className="mt-3 flex justify-end">
          <Pagination currentPage={page} onPageChange={setPage} totalPages={totalPages} showIcons />
        </div>
      </Card>
    </div>
  );
}